import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import type { AppState } from '../../../lib/store';
import { useRescueContext } from '../../lib/RescueContext';
import { US_STATES } from '../../lib/utils';
import {
  rescueCreateDeviceOrderMutation,
  RescueCreateDeviceOrderData,
  RescueCreateDeviceOrderInput,
} from '../../lib/rescueQueries';
import { trackDevicesOrderSubmitted } from '../../analytics';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput, PortalTextarea } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { ToggleGroup } from '../../components/FormField/ToggleGroup';
import { FormGrid } from '../../components/FormField/FormGrid';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

const QUANTITY_PRESETS = ['10', '20', '30', '40', '50', 'Other'] as const;
type QuantityChoice = (typeof QUANTITY_PRESETS)[number];
const DEFAULT_QUANTITY: QuantityChoice = '20';
const CUSTOM_QUANTITY_STEP = 10;
const CUSTOM_QUANTITY_MIN = 60;
const CUSTOM_QUANTITY_MAX = 100;

/** Pragmatic email shape check — backend remains authoritative. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface OrderDevicesModalProps {
  open: boolean;
  onClose: () => void;
}

interface SuccessState {
  orderId: string;
  deviceCount: number;
  includeHardCases: boolean;
}

export default function OrderDevicesModal({ open, onClose }: OrderDevicesModalProps) {
  const { orgName, shippingAddress } = useRescueContext();
  const session = useSelector((state: AppState) => state.session);
  const [createOrder] = useMutation<RescueCreateDeviceOrderData, { input: RescueCreateDeviceOrderInput }>(
    rescueCreateDeviceOrderMutation,
  );

  // Only pre-fill from the rescue's stored address when it's actually a clean
  // US address with a 2-letter state code that matches our `<select>` options.
  // Anything else (non-US country, full state name like "New York", missing
  // state) falls back to a blank form so the user can't accidentally submit a
  // country-corrupted order or get confused by an empty State select.
  // Memoized so the address-seed useEffect's dep array stays stable across
  // re-renders that don't actually change the underlying address.
  const validUSAddress = useMemo(() => {
    if (!shippingAddress) {
      return null;
    }
    if (shippingAddress.country !== 'US') {
      return null;
    }
    if (!shippingAddress.state || !US_STATES.includes(shippingAddress.state)) {
      return null;
    }
    return shippingAddress;
  }, [shippingAddress]);

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState(session?.email ?? '');
  const [quantityChoice, setQuantityChoice] = useState<QuantityChoice>(DEFAULT_QUANTITY);
  const [customQuantity, setCustomQuantity] = useState(CUSTOM_QUANTITY_MIN);
  const [includeHardCases, setIncludeHardCases] = useState(true);
  const [street, setStreet] = useState(validUSAddress?.line1 ?? '');
  const [apt, setApt] = useState(validUSAddress?.line2 ?? '');
  const [city, setCity] = useState(validUSAddress?.city ?? '');
  const [usState, setUsState] = useState(validUSAddress?.state ?? '');
  const [zip, setZip] = useState(validUSAddress?.zip ?? '');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SuccessState | null>(null);

  // Re-seed the address fields once per open cycle. Handles the case where the
  // org-info query resolves after the modal has already mounted.
  //
  // Known race: if the user opens the modal *and* starts typing in an address
  // field *before* org-info resolves, this effect will overwrite their input
  // when the data arrives. The window is small in practice — org-info is
  // fetched on app shell mount, well before anyone clicks "Order Devices" —
  // so we accept it. If it ever causes real complaints, the fix is to flip
  // `seededAddressRef.current` to true on the first user keystroke in any
  // address field.
  const seededAddressRef = useRef(false);
  // Same once-per-open-cycle pattern for the contact email. `useState` only
  // captures `session?.email` at first render; if the Redux session resolves
  // after the modal has already mounted (rare but possible), the effect seeds
  // it when it arrives. Same accepted race as the address: typing before the
  // session resolves can be overwritten.
  const seededEmailRef = useRef(false);
  // Synchronous guard against double-submit. The `submitting` state alone
  // can't prevent it: two rapid clicks landing before React commits the
  // first `setSubmitting(true)` would both pass `disabled={submitting}`.
  const submittingRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededAddressRef.current = false;
      return;
    }
    if (validUSAddress && !seededAddressRef.current) {
      setStreet(validUSAddress.line1 ?? '');
      setApt(validUSAddress.line2 ?? '');
      setCity(validUSAddress.city ?? '');
      setUsState(validUSAddress.state ?? '');
      setZip(validUSAddress.zip ?? '');
      seededAddressRef.current = true;
    }
  }, [open, validUSAddress]);
  useEffect(() => {
    if (!open) {
      seededEmailRef.current = false;
      return;
    }
    if (session?.email && !seededEmailRef.current) {
      setContactEmail(session.email);
      seededEmailRef.current = true;
    }
  }, [open, session?.email]);

  function resetForm() {
    setContactName('');
    setContactEmail(session?.email ?? '');
    setQuantityChoice(DEFAULT_QUANTITY);
    setCustomQuantity(CUSTOM_QUANTITY_MIN);
    setIncludeHardCases(true);
    setStreet(validUSAddress?.line1 ?? '');
    setApt(validUSAddress?.line2 ?? '');
    setCity(validUSAddress?.city ?? '');
    setUsState(validUSAddress?.state ?? '');
    setZip(validUSAddress?.zip ?? '');
    setNotes('');
    setShowErrors(false);
    setSubmitError(null);
    setSubmitting(false);
    setSuccess(null);
    // Belt-and-suspenders: ensure the synchronous gate is clean for the next
    // open even if a close ever slipped through while a submit was in flight.
    submittingRef.current = false;
  }

  function handleClose() {
    // Read the synchronous ref, not the React `submitting` state. A backdrop
    // / Escape / ✕ click landing right after Submit (before React has
    // committed `setSubmitting(true)`) would otherwise sneak past, letting the
    // modal close mid-mutation and triggering late `setSuccess` / `setSubmitError`
    // updates after unmount.
    if (submittingRef.current) {
      return;
    }
    resetForm();
    onClose();
  }

  const isOther = quantityChoice === 'Other';
  const deviceCount = isOther ? customQuantity : Number.parseInt(quantityChoice, 10);
  const trimmedEmail = contactEmail.trim();

  const errors = {
    contactName: contactName.trim() === '',
    contactEmailMissing: trimmedEmail === '',
    contactEmailInvalid: trimmedEmail !== '' && !EMAIL_RE.test(trimmedEmail),
    deviceCount: !Number.isFinite(deviceCount) || deviceCount <= 0,
    street: street.trim() === '',
    city: city.trim() === '',
    state: usState === '',
    zip: zip.trim() === '',
  };
  const hasErrors = Object.values(errors).some(Boolean);

  async function handleSubmit() {
    if (submittingRef.current) {
      return;
    }
    if (hasErrors) {
      setSubmitError(null);
      setShowErrors(true);
      return;
    }
    submittingRef.current = true;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await createOrder({
        variables: {
          input: {
            contactName: contactName.trim(),
            contactEmail: trimmedEmail,
            deviceQuantity: deviceCount,
            includeHardCases,
            shippingAddress: {
              line1: street.trim(),
              line2: apt.trim() ? apt.trim() : null,
              city: city.trim(),
              state: usState,
              // US-only — the form's state list is US states, so country is
              // hardcoded. Schema requires a non-null country string.
              country: 'US',
              zip: zip.trim(),
            },
            notes: notes.trim() ? notes.trim() : null,
          },
        },
      });
      const data = result.data?.rescueCreateDeviceOrder;
      if (!data?.ok) {
        throw new Error('Order submission was not accepted');
      }
      trackDevicesOrderSubmitted(deviceCount, includeHardCases);
      setSuccess({ orderId: data.orderId, deviceCount, includeHardCases });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit order request');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  function decrementCustom() {
    setCustomQuantity((prev) => Math.max(CUSTOM_QUANTITY_MIN, prev - CUSTOM_QUANTITY_STEP));
  }

  function incrementCustom() {
    setCustomQuantity((prev) => Math.min(CUSTOM_QUANTITY_MAX, prev + CUSTOM_QUANTITY_STEP));
  }

  if (success) {
    const { orderId, deviceCount: confirmedCount, includeHardCases: confirmedHardCases } = success;
    return (
      <Modal open={open} onClose={handleClose} size="wide">
        <div className={styles.orderConfirmState}>
          <div className={styles.orderConfirmIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5 12.5L10 17.5L19 7.5"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className={styles.orderConfirmTitle}>Request Submitted</h2>
          <p className={styles.orderConfirmDesc}>
            Your order for{' '}
            <strong>
              {confirmedCount} Fi Mini device{confirmedCount === 1 ? '' : 's'}
            </strong>
            {confirmedHardCases ? ' with hard cases' : ''} has been submitted for approval. You'll receive an email
            confirmation once approved.
          </p>
          <div className={styles.orderConfirmFooter}>
            <div className={styles.orderConfirmOrderNumber}>Order #{orderId}</div>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      <ModalTitle>Order Fi Devices</ModalTitle>
      <p className={styles.modalDescription}>
        Request Fi Mini devices for <strong>{orgName || 'your rescue'}</strong>. Orders are reviewed and typically
        approved within 1 business day.
      </p>

      <FormGrid>
        <PortalField
          label="Contact Name *"
          error="Please provide a contact name"
          showError={showErrors && errors.contactName}
        >
          <PortalInput
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Full name"
            hasError={showErrors && errors.contactName}
          />
        </PortalField>
        <PortalField
          label="Contact Email *"
          error={errors.contactEmailMissing ? 'Please provide a contact email' : 'Please provide a valid email address'}
          showError={showErrors && (errors.contactEmailMissing || errors.contactEmailInvalid)}
        >
          <PortalInput
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="you@example.org"
            hasError={showErrors && (errors.contactEmailMissing || errors.contactEmailInvalid)}
          />
        </PortalField>
      </FormGrid>

      <PortalField
        label="Number of Devices *"
        hint='Devices ship in batches of 10, up to 100 per order. Selecting "Other" allows custom quantities in increments of 10.'
      >
        <ToggleGroup
          options={QUANTITY_PRESETS.map((q) => ({ value: q, label: q }))}
          value={quantityChoice}
          onChange={(v) => setQuantityChoice(v as QuantityChoice)}
        />
        {isOther && (
          <div className={styles.customQuantityRow}>
            <Button
              variant="ghost"
              size="sm"
              onClick={decrementCustom}
              disabled={customQuantity <= CUSTOM_QUANTITY_MIN}
              className={styles.customQuantityButton}
              aria-label="Decrease quantity"
            >
              &minus;
            </Button>
            <span className={styles.customQuantityValue}>{customQuantity}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={incrementCustom}
              disabled={customQuantity >= CUSTOM_QUANTITY_MAX}
              className={styles.customQuantityButton}
              aria-label="Increase quantity"
            >
              +
            </Button>
          </div>
        )}
      </PortalField>

      <button
        type="button"
        className={styles.orderCheckboxRow}
        onClick={() => setIncludeHardCases((v) => !v)}
        aria-pressed={includeHardCases}
      >
        <span
          className={`${styles.orderCheckbox} ${includeHardCases ? styles.orderCheckboxChecked : ''}`}
          aria-hidden="true"
        >
          {includeHardCases && (
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M3.5 8.5L6.5 11.5L12.5 4.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
        <span>
          <span className={styles.orderCheckboxLabel}>Include hard cases</span>
          <span className={styles.orderCheckboxHint}>Ship 1 protective hard case per device</span>
        </span>
      </button>

      <div className={styles.orderSectionHeading}>Shipping Address</div>
      <FormGrid>
        <PortalField
          label="Street Address *"
          fullWidth
          error="Please provide a street address"
          showError={showErrors && errors.street}
        >
          <PortalInput
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            placeholder="123 Main Street"
            hasError={showErrors && errors.street}
          />
        </PortalField>
        <PortalField label="Apt / Suite">
          <PortalInput value={apt} onChange={(e) => setApt(e.target.value)} placeholder="Apt / Suite" />
        </PortalField>
        <PortalField label="City *" error="Please provide a city" showError={showErrors && errors.city}>
          <PortalInput
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            hasError={showErrors && errors.city}
          />
        </PortalField>
        <PortalField label="State *" error="Please select a state" showError={showErrors && errors.state}>
          <PortalSelect
            value={usState}
            onChange={(e) => setUsState(e.target.value)}
            hasError={showErrors && errors.state}
          >
            <option value="">Select…</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </PortalSelect>
        </PortalField>
        <PortalField label="Zip Code *" error="Please provide a zip code" showError={showErrors && errors.zip}>
          <PortalInput
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            placeholder="ZIP"
            inputMode="numeric"
            hasError={showErrors && errors.zip}
          />
        </PortalField>
      </FormGrid>

      <PortalField label="Notes">
        <PortalTextarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional — e.g., upcoming adoption event, urgency, special instructions"
        />
      </PortalField>

      {submitError && <div className={styles.errorContainer}>{submitError}</div>}

      <ModalActions>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit Request'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
